import React, {useEffect, useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Badge, Empty, NavBar, PullToRefresh, Skeleton, Tabs, Tag} from 'antd-mobile';
import {ArrowLeftOutlined, HistoryOutlined} from '@ant-design/icons';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {DspPath} from '../../../../enums/DeliveryNavigatePathEnum';
import {getNavBarContainerStyle} from '../../../../utils/deviceUtils';
import useDspWarehouseStore from '../../store/dspWarehouseStore';
import useEnsureDspContext from '../../hooks/useEnsureDspContext';
import {
    GET_CUSTOMER_COMPLAINT_LIST_BY_FILTERS,
    GetCustomerComplaintListByFiltersResponse,
    GetCustomerComplaintListByFiltersVariables,
} from './graphql/queries/getCustomerComplaintListByFilters';
import {
    ComplaintStatus,
    CustomerComplaint,
    CustomerComplaintTab,
    DSP_VISIBLE_COMPLAINT_TYPES,
    TAB_TO_STATUS_LIST,
} from './graphql/types/CustomerComplaintType';
import {
    GET_DSP_COMPLAINT_TOTAL_UNREAD_COUNT,
    GetDspComplaintTotalUnreadCountResponse,
    GetDspComplaintTotalUnreadCountVariables,
} from './graphql/queries/getDspComplaintHistoryUnreadCount';
import ComplaintFilterBar from './components/ComplaintFilterBar';
import ComplaintCard from './components/ComplaintCard';
import {formatTime, isNearDue, isOverdue, matchKeyword} from './utils/complaintListUtils';
import {BarCodeScanner} from '../../../../components/BarCodeScanner';
import {useNetwork} from '../../../../hooks/useNetwork';

// 两个 tab 涉及的全部状态合集，一次性拉回后前端按 tab 分组
const ALL_OPEN_STATUSES: string[] = [
    ...TAB_TO_STATUS_LIST[CustomerComplaintTab.PENDING],
    ...TAB_TO_STATUS_LIST[CustomerComplaintTab.APPEALING],
];

const CustomerComplaintListPage: React.FC = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();

    const {isOnline} = useNetwork();
    const location = useLocation();
    // 刷新 / URL 直达本页时未经过首页，确保 store 的 dspId / warehouses 就绪，否则下方查询会被 skip
    useEnsureDspContext();
    const dspId = useDspWarehouseStore((state) => state.dspId);
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const warehouseId = useMemo(() => {
        if (!selectedWarehouseId) return undefined;
        const n = Number(selectedWarehouseId);
        return Number.isFinite(n) ? n : undefined;
    }, [selectedWarehouseId]);

    const {data: unreadResp, refetch: refetchUnread} = useQuery<
        GetDspComplaintTotalUnreadCountResponse,
        GetDspComplaintTotalUnreadCountVariables
    >(GET_DSP_COMPLAINT_TOTAL_UNREAD_COUNT, {
        skip: !dspId || warehouseId == null,
        variables: {dspId: dspId ?? 0, warehouseId: warehouseId ?? 0},
        fetchPolicy: 'no-cache',
    });
    const totalUnread = unreadResp?.getDspComplaintHistoryUnreadCount?.totalUnread ?? 0;

    // 从历史页清掉红点后返回本页时（location 变化），重查总未读，保证右上角角标与历史页同步
    useEffect(() => {
        if (!dspId || warehouseId == null) return;
        refetchUnread().catch(() => undefined);
    }, [location.key, dspId, warehouseId, refetchUnread]);

    const [activeTab, setActiveTab] = useState<CustomerComplaintTab>(CustomerComplaintTab.PENDING);
    const [keyword, setKeyword] = useState('');
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
    const [selectedType, setSelectedType] = useState<string | null>(null);

    const createdAtRange = useMemo(() => {
        if (!dateRange) return null;
        const [start, end] = dateRange;
        const s = new Date(start);
        s.setHours(0, 0, 0, 0);
        const e = new Date(end);
        e.setHours(23, 59, 59, 999);
        return {from: s.toISOString(), to: e.toISOString()};
    }, [dateRange]);

    const listFilter = useMemo(
        () => ({
            dspId: dspId ?? undefined,
            warehouseId: warehouseId ?? 0,
            isFinished: 0,
            statusList: ALL_OPEN_STATUSES,
            complaintType: selectedType ?? undefined,
            createdAtFrom: createdAtRange?.from,
            createdAtTo: createdAtRange?.to,
            sortBy: 'APPEAL_DEADLINE_AT' as const,
            sortDirection: 'ASC' as const,
        }),
        [dspId, warehouseId, selectedType, createdAtRange],
    );

    const {data, loading, refetch} = useQuery<
        GetCustomerComplaintListByFiltersResponse,
        GetCustomerComplaintListByFiltersVariables
    >(GET_CUSTOMER_COMPLAINT_LIST_BY_FILTERS, {
        skip: !dspId || warehouseId == null,
        variables: {filter: listFilter},
        fetchPolicy: 'no-cache',
    });

    const items = useMemo(() => data?.getCustomerComplaintListByFilters ?? [], [data]);

    const handleRefresh = async () => {
        if (!dspId) return;
        await refetch({filter: listFilter});
        await refetchUnread().catch(() => undefined);
    };

    // 当前页面一期只展示 DNR（签收未收到）；可见类型由 DSP_VISIBLE_COMPLAINT_TYPES 收口
    const complaintTypeOptions = DSP_VISIBLE_COMPLAINT_TYPES;

    // 先按筛选条件过滤，再按 tab 分组；两个 tab 的 count 都基于同一份过滤后的数据
    const filteredByFilters = useMemo(() => {
        return items.filter((it: CustomerComplaint) => {
            if (!matchKeyword(it, keyword)) return false;
            return true;
        });
    }, [items, keyword]);

    const pendingItems = useMemo(
        () => filteredByFilters.filter((it) => TAB_TO_STATUS_LIST[CustomerComplaintTab.PENDING].includes(it.status)),
        [filteredByFilters],
    );
    const appealingItems = useMemo(
        () => filteredByFilters.filter((it) => TAB_TO_STATUS_LIST[CustomerComplaintTab.APPEALING].includes(it.status)),
        [filteredByFilters],
    );
    const filteredItems = activeTab === CustomerComplaintTab.PENDING ? pendingItems : appealingItems;

    const renderInfoRow = (item: CustomerComplaint) => {
        const pending = item.status === ComplaintStatus.PENDING_DSP_ACTION;
        if (pending) {
            const warn = isOverdue(item.appealDeadlineAt) || isNearDue(item.appealDeadlineAt);
            return (
                <div className={`text-sm ${warn ? 'text-red-500' : 'text-black'}`}>
                    {t('pages.dsp.home.customerComplaint.list.deadline', '处理时效')}：
                    {t('pages.dsp.home.customerComplaint.list.dueBefore', '请在{{time}}前处理', {
                        time: formatTime(item.appealDeadlineAt),
                    })}
                </div>
            );
        }
        return (
            <div className="text-sm text-black">
                {t('pages.dsp.home.customerComplaint.list.appealTime', '申诉时间')}：
                {formatTime(item.appealSubmittedAt)}
            </div>
        );
    };

    const renderTag = (item: CustomerComplaint) => {
        if (item.status !== ComplaintStatus.PENDING_DSP_ACTION) return null;
        if (isOverdue(item.appealDeadlineAt)) {
            return (
                <Tag color="danger" style={{borderRadius: 4}}>
                    {t('pages.dsp.home.customerComplaint.list.overdue', '已超期')}
                </Tag>
            );
        }
        if (isNearDue(item.appealDeadlineAt)) {
            return (
                <Tag color="danger" style={{borderRadius: 4}}>
                    {t('pages.dsp.home.customerComplaint.list.nearDue', '即将超期')}
                </Tag>
            );
        }
        return null;
    };

    return (
        <div className="bg-gray-50 min-h-screen">
            {/* 全局扫码层：ComplaintFilterBar 点扫码图标 setIsVisible(true) 后由此渲染相机 */}
            {isOnline && <BarCodeScanner showManualInputIcon={false} />}
            <div {...getNavBarContainerStyle()} className="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
                <NavBar
                    onBack={() => navigate(DspPath.DELIVERY_DSP_ISSUE_PARCEL_WORKBENCH)}
                    backIcon={<ArrowLeftOutlined />}
                    right={
                        <div
                            className="flex items-center justify-center pr-2 cursor-pointer"
                            onClick={() => navigate(DspPath.DELIVERY_DSP_COMPLAINT_HISTORY)}
                            aria-label={t('pages.dsp.home.customerComplaint.history.entryAria', '历史问题件') as string}
                        >
                            <Badge content={totalUnread > 0 ? totalUnread : null}>
                                <HistoryOutlined style={{fontSize: 22}} />
                            </Badge>
                        </div>
                    }
                >
                    {t('pages.dsp.home.customerComplaint.title', '问题件处理')}
                </NavBar>
            </div>

            <div className="pt-12 bg-white">
                <ComplaintFilterBar
                    keyword={keyword}
                    onKeywordChange={setKeyword}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    selectedType={selectedType}
                    onSelectedTypeChange={setSelectedType}
                    complaintTypeOptions={complaintTypeOptions}
                />

                <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as CustomerComplaintTab)}>
                    <Tabs.Tab
                        title={`${t('pages.dsp.home.customerComplaint.tabs.PENDING', '待处理')}(${pendingItems.length})`}
                        key={CustomerComplaintTab.PENDING}
                    />
                    <Tabs.Tab
                        title={`${t('pages.dsp.home.customerComplaint.tabs.APPEALING', '申诉中')}(${appealingItems.length})`}
                        key={CustomerComplaintTab.APPEALING}
                    />
                </Tabs>
            </div>

            <PullToRefresh onRefresh={handleRefresh}>
                {loading && items.length === 0 ? (
                    <Skeleton.Paragraph lineCount={6} animated />
                ) : filteredItems.length === 0 ? (
                    <Empty description={t('pages.dsp.home.customerComplaint.empty', '暂无数据')} />
                ) : (
                    <div className="px-3 py-2 space-y-2">
                        {filteredItems.map((item: CustomerComplaint) => (
                            <ComplaintCard
                                key={item.id}
                                item={item}
                                tag={renderTag(item)}
                                infoRow={renderInfoRow(item)}
                                onClick={() =>
                                    navigate(
                                        DspPath.DELIVERY_DSP_COMPLAINT_DETAIL.replace(
                                            ':complaintId',
                                            String(item.id),
                                        ),
                                    )
                                }
                            />
                        ))}
                    </div>
                )}
            </PullToRefresh>
        </div>
    );
};

export default CustomerComplaintListPage;
